import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useGlobalContext} from "../../../contexts/globalContext";
import {useTranslation} from "react-i18next";
import Heading from "../../../components/Heading";
import Loader from "../../../components/Loader";
import {StoredIndividualization} from "../../../types/Individualization";
import CreateCard from "../../Settings/FontSettings/CreateCard";
import {useIndividualizationContext} from "../../../contexts/individualizationContext";
import {useFetchData} from "../../../hooks/useFetchData";
import {useToggleVisibility} from "../../../hooks/useToggleVisibility";
import cx from "classnames";
import styles from './Overview.module.css'
import IndividualizationCard from "./IndividualizationCard";
import DeleteIndividualizationModal from "./DeleteIndividualizationModal";
import IndividualizationListView from "./IndividualizationListView";
import IconButton from "../../../components/IconButton";
import {faList, faThLarge} from "@fortawesome/free-solid-svg-icons";
import Button from "../../../components/Button";
import Input from "../../../components/Input";
import Pagination from "../../../components/Pagination";

const IndividualizationOverview: React.FC= () => {
    const { currentTab, changeCurrentTab } = useIndividualizationContext()
    const { showToast, showModal } = useGlobalContext()
    const { t } = useTranslation(['individualization'])

    const [viewMode, setViewMode] = useState<'card' | 'list'>('card');
    const [searchValue, setSelectedArticle] = useState<string>("");
    const [debounceSearch, setDebounceSearch] = useState<string>("");
    const [currentPage, setCurrentPage] = useState<number>(1);
    const itemsPerPage = useMemo(() => 12, []);

    const {
        data: individualization,
        fetch,
        isLoading,
        pagination,
    } = useFetchData<StoredIndividualization>('individualization')

    const { toggle } = useToggleVisibility('individualizationToggle')

    const getIndividualizationById = useCallback((individualization_id: number) => {
        return individualization.find((data) => data.id === individualization_id)
    }, [individualization])

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebounceSearch(searchValue)
        }, 750)
        return () => clearTimeout(timer)
    }, [searchValue])

    useEffect(() => {
        if (currentTab === 1) {
            fetch({page: currentPage.toString(), search: debounceSearch, perPage: itemsPerPage.toString()})
        }
    }, [fetch, currentPage, debounceSearch, currentTab, itemsPerPage]);

    const openDeleteModal = (individualization_id: number) => {
        const element = getIndividualizationById(individualization_id)
        if (element) {
            const content: JSX.Element = <DeleteIndividualizationModal
                individualization={element}
                onSubmit={() => fetch({page: currentPage.toString(), search: debounceSearch, perPage: itemsPerPage.toString()})}
            />
            showModal({heading: t('overview.delete_modal.heading'), content: content, fullscreen: false})
        }
    }

    const toggleVisibility = (individualization_id: number) => {
        const element = getIndividualizationById(individualization_id)
        if (element) {
            toggle(element).then((res) => {
                if (res.status === 200 && res.data?.data) {
                    showToast('top-right', 'success', t('overview.toggle_visibility.success'))
                } else {
                    showToast('top-right', 'error', t('overview.toggle_visibility.error'))
                }
                fetch()
            })
        }
    }

    return currentTab === 1 ? (
        <div>
            <Heading level={2} classes={'mt-4'}>{t('overview.heading')}</Heading>
            <div className={'d-flex justify-content-between align-items-center flex-wrap mb-4'} style={{gap: '1rem'}}>
                <Input
                    type="text"
                    placeholder={t('overview.delete_modal.search')}
                    value={searchValue}
                    onChange={(_, value) => {
                        setSelectedArticle(value)
                        setCurrentPage(1)
                    }}
                    property={'search'}
                    classes={'w-25'}
                    style={{minWidth: '200px'}}
                />
                <div className="d-flex flex-wrap">
                    <Button
                        hierarchy={'primary'}
                        text={t('overview.add')}
                        fn={() => changeCurrentTab(2)}
                        style={{ padding: '0.5em 1.8em', marginRight: '1rem' }}
                    />
                    <IconButton
                        icon={faThLarge}
                        color={viewMode === 'card' ? "#fc0" : "#2f2f2f"}
                        title={t('overview.cardView')}
                        style={{fontSize: '1.25rem'}}
                        onClick={() => setViewMode('card')}
                    />
                    <IconButton
                        icon={faList}
                        color={viewMode === 'list' ? "#fc0" : "#2f2f2f"}
                        title={t('overview.listView')}
                        style={{fontSize: '1.25rem'}}
                        onClick={() => setViewMode('list')}
                    />
                </div>
            </div>

            {isLoading ? (
                <div className="w-100 d-flex justify-content-center">
                    <Loader isLoading={isLoading} size="m" />
                </div>
            ) : viewMode === 'card' ? (
                <div className={cx(styles['card-list'])}>
                    {individualization.length === 0 && (
                        <CreateCard
                            text={t('overview.add')}
                            openCreateModal={() => changeCurrentTab(2)}
                        />
                    )}
                    {individualization.map((data: StoredIndividualization) => (
                        <IndividualizationCard
                            key={data.id}
                            individualization={data}
                            toggleVisibility={toggleVisibility}
                            openDeleteModal={openDeleteModal}
                        />
                    ))}
                </div>
            ) : (
                <div>
                    <IndividualizationListView
                        individualizations={individualization}
                        toggleVisibility={toggleVisibility}
                        openDeleteModal={openDeleteModal}
                    />
                </div>
            )}
            <div>
                {(pagination && pagination.lastPage > 1) && <Pagination
                    totalPages={pagination.lastPage}
                    currentPage={currentPage}
                    onPageChange={(p: number) => setCurrentPage(p)}
                    classes={'mt-4'}
                />}
            </div>
        </div>
    ) : null;
};

export default IndividualizationOverview;